import { cn } from '@invent/wl-ui-kit-next';
import { Input } from '@invent/wl-ui-kit-next/ui';
import { X } from 'lucide-react';
import * as React from 'react';

import { listsApi } from '../../api/lists-api';
import { refusalMessage } from '../../refusal';

/** How long the typing rests before a read goes out. */
const READ_AFTER_MS = 200;

/** survey-api's limits on `q` (`SearchTerms`, whose words part at spaces and commas): a longer
 *  needle is not read, rather than refused. */
const MAX_NEEDLE_LENGTH = 200;
const MAX_NEEDLE_WORDS = 8;

/** One value the rows hold, and how many rows hold it. */
interface ISuggestion {
  value: string;
  count: number;
}

type SuggestionsRead =
  | { state: 'reading' }
  | { state: 'read'; needle: string; values: ISuggestion[]; truncated: boolean }
  | { state: 'failed'; reason: string };

/**
 * A text field's value typed with the values the survey's rows already hold offered under the box
 * (SR-419, AC2) — a condition's value in *Advanced…* and a report's filter. The values are
 * survey-api's facets (`POST /lists/facets`, the column funnel's read): distinct, case folded as the
 * filter itself matches (SR-417), the commonest first; typed text narrows them to the values
 * **containing** it, those **beginning** with it first. Picking one writes it exactly as stored, so
 * the condition matches the rows it was picked from; anything else typed is kept as typed.
 *
 * A long text (`typedOnly`) is read only once something is typed — its values run long, and an
 * unnarrowed read would fetch hundreds of them — and a value of several lines is never offered,
 * since a one-line box cannot hold it.
 *
 * The box is an ARIA combobox: ↓/↑ move through the values, Enter or a click picks, Escape shuts
 * the list (and only the list — not the sheet around it). A failed read is said in the list, the
 * typing kept.
 */
export function SuggestInput({
  label,
  value,
  survey,
  field,
  typedOnly,
  invalid,
  onChange
}: {
  label: string;
  value: string;
  survey: string;
  field: string;
  typedOnly: boolean;
  invalid: boolean;
  onChange: (value: string) => void;
}) {
  return (
    <SuggestBox
      label={label}
      text={value}
      survey={survey}
      field={field}
      typedOnly={typedOnly}
      invalid={invalid}
      className="h-8 w-56"
      onText={onChange}
      onPick={onChange}
    />
  );
}

/**
 * "Is any of" on a text field: the values as chips, each with its ✕ (`TagInput`'s shape), and a box
 * after them that suggests as `SuggestInput` does. A value picked or typed (Enter, a comma, or
 * leaving the box) becomes a chip; Backspace in the empty box takes the last one back. A value
 * already chosen is not offered again.
 */
export function SuggestTags({
  label,
  values,
  survey,
  field,
  typedOnly,
  invalid,
  onChange
}: {
  label: string;
  values: readonly string[];
  survey: string;
  field: string;
  typedOnly: boolean;
  invalid: boolean;
  onChange: (values: string[]) => void;
}) {
  const [draft, setDraft] = React.useState('');
  const add = (value: string) => {
    setDraft('');
    const trimmed = value.trim();
    if (trimmed !== '') onChange([...values, trimmed]);
  };

  return (
    <div
      role="group"
      aria-label={label}
      className={cn(
        'flex min-h-9 w-72 flex-wrap items-center gap-1 rounded-md border bg-white px-1.5 py-1',
        invalid ? 'border-destructive' : 'border-border'
      )}
    >
      {values.map((value, index) => (
        <span
          key={index}
          className="inline-flex items-center gap-1 rounded bg-[var(--color-off-white)] py-0.5 pr-0.5 pl-1.5"
        >
          {value}
          <button
            type="button"
            aria-label={`Remove ${value}`}
            className="grid size-4 place-items-center rounded text-muted-foreground hover:text-foreground"
            onClick={() => onChange(values.filter((_, at) => at !== index))}
          >
            <X className="size-3" aria-hidden />
          </button>
        </span>
      ))}
      <SuggestBox
        label="Add a value"
        text={draft}
        survey={survey}
        field={field}
        typedOnly={typedOnly}
        invalid={invalid}
        exclude={values}
        className="h-7 min-w-24 flex-1 border-0 px-1 shadow-none focus-visible:ring-0"
        wrapperClassName="min-w-24 flex-1"
        onText={setDraft}
        onPick={add}
        onCommit={() => add(draft)}
        onBackspaceEmpty={() => values.length > 0 && onChange(values.slice(0, -1))}
      />
    </div>
  );
}

/** Whether `needle` is read at all: within survey-api's limits, and — for a long text — typed. */
function readable(needle: string, typedOnly: boolean): boolean {
  if (typedOnly && needle === '') return false;
  return needle.length <= MAX_NEEDLE_LENGTH && needle.split(/[\s,]+/).filter(Boolean).length <= MAX_NEEDLE_WORDS;
}

function SuggestBox({
  label,
  text,
  survey,
  field,
  typedOnly,
  invalid,
  exclude,
  className,
  wrapperClassName,
  onText,
  onPick,
  onCommit,
  onBackspaceEmpty
}: {
  label: string;
  text: string;
  survey: string;
  field: string;
  typedOnly: boolean;
  invalid: boolean;
  exclude?: readonly string[];
  className?: string;
  wrapperClassName?: string;
  onText: (text: string) => void;
  onPick: (value: string) => void;
  /** What was typed taken as a value — the tags' Enter with nothing highlighted, a comma, a blur. */
  onCommit?: () => void;
  onBackspaceEmpty?: () => void;
}) {
  const id = React.useId();
  const [open, setOpen] = React.useState(false);
  const [active, setActive] = React.useState(-1);
  // The read narrows by what is typed, not by a value the box was drawn with or a pick just wrote:
  // a box opened on "Morgan Stanley" lists every value, as an empty one does.
  const [typing, setTyping] = React.useState(false);
  const needle = typing ? text.trim() : '';
  const read = useSuggestions(open && readable(needle, typedOnly) ? survey : null, field, needle);
  const values =
    read?.state === 'read'
      ? read.values.filter(
          (one) =>
            !/[\r\n]/.test(one.value) &&
            !exclude?.some((held) => held.toLowerCase() === one.value.toLowerCase())
        )
      : [];
  // The previous needle's values stay drawn, dimmed, while the typed one is read — never picked:
  // ↓ and Enter act on the values that answer the words on screen.
  const live = read?.state === 'read' && read.needle === needle;
  const pickable = live ? values : [];
  React.useEffect(() => setActive(-1), [read]);

  // Escape shuts the list, and the sheet the box sits in must not hear it: the sheet listens on the
  // document in the capture phase, so the list answers first, on the window.
  React.useEffect(() => {
    if (!open) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      event.stopPropagation();
      setOpen(false);
    };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, [open]);

  const pick = (value: string) => {
    onPick(value);
    setTyping(false);
    setActive(-1);
    setOpen(false);
  };

  const onKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      if (!open) {
        setOpen(true);
        return;
      }
      if (pickable.length === 0) return;
      const step = event.key === 'ArrowDown' ? 1 : -1;
      setActive((current) => (current + step + pickable.length) % pickable.length);
    } else if (event.key === 'Enter') {
      if (open && active >= 0 && active < pickable.length) {
        // A pick, not the form's submit.
        event.preventDefault();
        pick(pickable[active].value);
      } else if (onCommit) {
        event.preventDefault();
        onCommit();
        setTyping(false);
      }
    } else if (event.key === ',' && onCommit) {
      event.preventDefault();
      onCommit();
      setTyping(false);
    } else if (event.key === 'Backspace' && text === '' && onBackspaceEmpty) {
      event.preventDefault();
      onBackspaceEmpty();
    }
  };

  const listId = `${id}-list`;
  const optionId = (index: number) => `${id}-option-${index}`;
  return (
    <span className={cn('relative inline-flex', wrapperClassName)}>
      <Input
        aria-label={label}
        aria-invalid={invalid || undefined}
        role="combobox"
        aria-autocomplete="list"
        aria-expanded={open && read !== null}
        aria-controls={listId}
        aria-activedescendant={open && active >= 0 ? optionId(active) : undefined}
        autoComplete="off"
        value={text}
        onFocus={() => setOpen(true)}
        onClick={() => setOpen(true)}
        onChange={(event) => {
          onText(event.target.value);
          setTyping(true);
          setActive(-1);
          setOpen(true);
        }}
        onKeyDown={onKeyDown}
        onBlur={() => {
          setOpen(false);
          setTyping(false);
          onCommit?.();
        }}
        className={className}
      />
      {open && read && (
        <div
          className="absolute top-full left-0 z-50 mt-1 grid w-max min-w-full max-w-[24rem] gap-1 rounded-md border border-border bg-popover p-1 text-[0.8125rem] shadow-md"
          // Down, not click: the box keeps its focus, so its blur does not shut the list first.
          onMouseDown={(event) => event.preventDefault()}
        >
          {read.state === 'reading' && <p className="px-2 py-1.5 text-muted-foreground">Reading the values…</p>}
          {read.state === 'failed' && (
            <p role="alert" className="px-2 py-1.5 text-destructive">
              {read.reason}
            </p>
          )}
          {live && values.length === 0 && (
            <p className="px-2 py-1.5 text-muted-foreground">
              {needle === '' ? 'No values in the records yet.' : 'No value in the records matches.'}
            </p>
          )}
          <ul
            id={listId}
            role="listbox"
            aria-label={`${label} — values in the records`}
            aria-busy={!live || undefined}
            className={cn('max-h-64 overflow-y-auto', !live && 'opacity-60')}
          >
            {values.map((one, index) => (
              <li
                key={one.value}
                id={optionId(index)}
                role="option"
                aria-selected={live && index === active}
                aria-labelledby={`${optionId(index)}-value`}
                onMouseDown={() => live && pick(one.value)}
                onMouseEnter={() => live && setActive(index)}
                className={cn(
                  'flex cursor-default items-baseline justify-between gap-4 rounded-sm px-2 py-1.5',
                  live && index === active && 'bg-accent text-accent-foreground'
                )}
              >
                <span id={`${optionId(index)}-value`} title={one.value} className="min-w-0 truncate">
                  <Marked text={one.value} needle={needle} />
                </span>
                <span className="shrink-0 text-xs tabular-nums text-muted-foreground">{one.count}</span>
              </li>
            ))}
          </ul>
          {live && read.truncated && (
            <p className="border-t border-border px-2 pt-1.5 pb-1 text-xs text-muted-foreground">
              More values than these — type to narrow.
            </p>
          )}
        </div>
      )}
    </span>
  );
}

/** The text with its first match of `needle` in bold, case ignored. */
function Marked({ text, needle }: { text: string; needle: string }) {
  const at = needle === '' ? -1 : text.toLowerCase().indexOf(needle.toLowerCase());
  if (at < 0) return <>{text}</>;
  return (
    <>
      {text.slice(0, at)}
      <span className="font-semibold">{text.slice(at, at + needle.length)}</span>
      {text.slice(at + needle.length)}
    </>
  );
}

/**
 * The field's values over the survey's rows, narrowed by `needle` — read when `survey` is set (the
 * list is open and the needle readable), after the typing rests, a newer read aborting the one
 * still out. Reads are kept for the box's life, so moving back to a needle already read answers at
 * once. Null while not read.
 */
function useSuggestions(survey: Nullable<string>, field: string, needle: string): Nullable<SuggestionsRead> {
  const cache = React.useRef(new Map<string, SuggestionsRead>());
  const key = `${survey}\n${field}\n${needle}`;
  const [read, setRead] = React.useState<Nullable<SuggestionsRead>>(null);
  React.useEffect(() => {
    if (survey === null) {
      setRead(null);
      return;
    }
    const held = cache.current.get(key);
    if (held) {
      setRead(held);
      return;
    }
    setRead((current) => (current?.state === 'read' ? current : { state: 'reading' }));
    const controller = new AbortController();
    const timer = window.setTimeout(() => {
      listsApi
        .facets({ query: { survey }, field, ...(needle ? { q: needle } : {}) }, controller.signal)
        .then((facets) => {
          if (controller.signal.aborted) return;
          const values = facets.values
            .filter((facet): facet is typeof facet & { value: string } => typeof facet.value === 'string')
            .map((facet) => ({ value: facet.value, count: facet.count }));
          const done: SuggestionsRead = { state: 'read', needle, values, truncated: facets.truncated };
          cache.current.set(key, done);
          setRead(done);
        })
        .catch((error: unknown) => {
          if (controller.signal.aborted) return;
          setRead({ state: 'failed', reason: refusalMessage(error, 'The values could not be read.') });
        });
    }, needle === '' ? 0 : READ_AFTER_MS);
    return () => {
      window.clearTimeout(timer);
      controller.abort();
    };
  }, [key, survey, field, needle]);
  return survey === null ? null : read;
}
